<template>
  <WdsStack direction-column class="tw-rounded">
    <WdsStack
      justify-space-between
      align-center
      class="tw-bg-true-gray-50 tw-border-b tw-border-gray-200 tw-px-4 tw-py-3"
    >
      <WdsHeading class="tw-pl-1 tw-text-lg tw-font-semibold" type="title3">
        {{ $t('Contract summary') }}
      </WdsHeading>
      <WdsButtonGhost
        v-if="hire?.conversation"
        :href="
          laravelNamedRoute('messages.show', { id: hire.conversation.id }) + '?managecontract=1'
        "
        secondary
        class="tw-text-primary tw-font-medium"
      >
        <WdsStack justify-center align-center gap-xs>
          {{ $t('Manage') }}
          <WdsIcon name="chevron-right" :size="16" primary />
        </WdsStack>
      </WdsButtonGhost>
    </WdsStack>

    <WdsStack v-if="loading" align-start>Loading</WdsStack>

    <template v-else-if="hire">
      <div class="tw-p-4">
        <WdsGrid columns="2" row-gap-md>
          <WdsText class="tw-font-medium tw-text-gray-600">{{ $t('Contract type') }}</WdsText>
          <WdsText class="tw-text-gray-900">
            <template v-if="hire.contractTypeShort">{{ hire.contractTypeShort }}</template>
            <template v-else>
              <span class="tw-text-orange-500">{{ $t('missing') }}</span>
            </template>
          </WdsText>

          <WdsText class="tw-font-medium tw-text-gray-600">{{ $t('Timeframe') }}</WdsText>
          <WdsText class="tw-text-gray-900">{{ timeLine.start }} - {{ timeLine.end }}</WdsText>

          <WdsText class="tw-font-medium tw-text-gray-600">{{ $t('Rate') }}</WdsText>
          <WdsText v-if="contract" class="tw-text-gray-900">
            {{ contract.currency }} {{ contract.rate }} /
            {{ contract?.rateType?.toLowerCase() ?? '' }}
          </WdsText>

          <WdsText class="tw-font-medium tw-text-gray-600">{{ $t('PO number') }}</WdsText>
          <WdsText :secondary="!contract?.purchaseOrderNumber" class="tw-text-gray-900">
            {{ contract?.purchaseOrderNumber ? contract.purchaseOrderNumber : $t('Not specified') }}
          </WdsText>

          <WdsText class="tw-font-medium tw-text-gray-600">
            {{ $t('Primary work location') }}
          </WdsText>
          <WdsText v-if="contract && contract.locationPreference" class="tw-text-gray-900">
            {{ locationPreference(contract.locationPreference) }}
          </WdsText>

          <WdsText class="tw-font-medium tw-text-gray-600">
            {{ $t('Work address') }}
          </WdsText>
          <WdsText :secondary="!contract?.location?.formattedAddress">
            {{ contract?.location?.formattedAddress ?? $t('Not specified') }}
          </WdsText>

          <WdsText class="tw-font-medium tw-text-gray-600">{{ $t('Role title') }}</WdsText>
          <WdsText v-if="contract?.jobName" class="tw-text-gray-900">
            {{ contract.jobName }}
          </WdsText>
          <WdsText class="tw-font-medium tw-text-gray-600">{{ $t('Role description') }}</WdsText>
          <div>
            <WdsText
              v-if="contract?.jobDescription"
              :text="contract?.jobDescription"
              :class="[
                'tw-whitespace-pre-wrap tw-transition-all tw-duration-200 tw-max-w-1/2',
                !isExpanded ? 'tw-line-clamp-2' : '',
              ]"
            />
            <WdsButtonGhost
              v-if="shouldShowToggle"
              secondary
              class="tw-font-medium tw-self-end"
              @click="isExpanded = !isExpanded"
            >
              <WdsStack justify-center align-center gap-xs>
                {{ isExpanded ? $t('Hide') : $t('See more') }}
                <WdsIcon :name="isExpanded ? 'chevron-up' : 'chevron-down'" :size="16" primary />
              </WdsStack>
            </WdsButtonGhost>
          </div>
        </WdsGrid>
      </div>
    </template>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsHeading,
  WdsStack,
  WdsIcon,
  WdsText,
  WdsButtonGhost,
  WdsGrid,
} from '@worksome/wds-components'
import { computed, ref, onMounted, nextTick, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useDate from '@/composables/useDate'
import { LocationPreference } from '@/gql/graphql'
import { convertEnumToDisplayText } from '@/utils/tools'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'

const props = defineProps<{
  hireId: string
}>()

// Reactive state
const isExpanded = ref<boolean>(false)
const shouldShowToggle = ref<boolean>(false)

const ContractFragment = graphql(`
  fragment ContractSummaryFields on Contract {
    id
    status
    jobName
    jobDescription
    startDate
    endDate
    rate
    rateType
    currency
    purchaseOrderNumber
    location {
      formattedAddress
    }
    address
    locationPreference
  }
`)

const HireFragment = graphql(`
  fragment HireContractSummaryFields on Hire {
    id
    contractTypeShort
    activeContract {
      ...ContractSummaryFields
    }
    latestContract {
      ...ContractSummaryFields
    }
    worker {
      id
      name
    }
    conversation {
      id
    }
  }
`)

const GET_HIRE = graphql(`
  query GetHireForContractSummary($id: ID!) {
    hire(id: $id) {
      ...HireContractSummaryFields
    }
  }
`)

const { result, loading } = useQuery(GET_HIRE, () => ({ id: props.hireId }), {
  clientId: 'publicApi',
})

const hire = computed(() => useFragment(HireFragment, result.value?.hire))
const contract = computed(() =>
  hire.value !== null && hire.value !== undefined
    ? useFragment(ContractFragment, hire.value.activeContract ?? hire.value.latestContract)
    : null,
)

const timeLine = computed(() => {
  if (contract.value === null) return { start: '', end: '' }

  const { date: start } = useDate(contract.value.startDate ?? '')
  const { date: end } = useDate(contract.value.endDate ?? '')

  return { start, end }
})

// Function to check if text needs truncation
const checkTextOverflow = async () => {
  if (!contract.value?.jobDescription) return

  await nextTick()
  const textElement = document.querySelector('.tw-line-clamp-2') as HTMLElement
  if (!textElement) return

  // Get the original text height and line height
  const computedStyle = window.getComputedStyle(textElement)
  const lineHeight = parseInt(computedStyle.lineHeight, 10)
  const height = textElement.scrollHeight

  // If text height is greater than 2 lines, show toggle
  shouldShowToggle.value = height > lineHeight * 2
}

const locationPreference = (location: LocationPreference) => {
  switch (location) {
    case LocationPreference.OnsiteOnly:
      return $t('Onsite')
    case LocationPreference.RemoteOnly:
      return $t('Remote')
    default:
      return $t(convertEnumToDisplayText(location))
  }
}

// Watch for contract changes to recheck text overflow
watch(
  () => contract.value?.jobDescription,
  () => {
    isExpanded.value = false
    checkTextOverflow()
  },
)

onMounted(() => {
  checkTextOverflow()
})
</script>
